import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Hours, RouteLink, Tooltip, Widget } from '~/components';
import { useApi, useMember, useWorkspace } from '~/contexts';
import { dateFormats } from '~/utils';
import {
  Box,
  Boxes,
  ChartContainer,
  Label,
  LowerBoxes,
  Metric,
  SmallBox,
  UpperBox,
  Value,
} from '../components/StyledComponents';
import ViewNav from '../components/ViewNav';
import MyHoursByMonthChart from './MyHoursByMonthChart';
import MyHoursProgressChart from './MyHoursProgressChart';
import { useHoursFormat, useTimeEntriesReportURL } from '~/hooks';

export default function MyHoursWidget({ project, view, actualsThrough, onViewChange }) {
  const { workspace } = useWorkspace();
  const { member } = useMember();

  const api = useApi();
  const [{ data, isReady }, setQuery] = useState({ data: null, isReady: false });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .dashboard()
        .progress.myHours({ actualsThrough });
      setQuery({ data, isReady: true });
    } catch (error) {
      setQuery({ data: null, isReady: true });
    }
  }, [workspace.id, project.id, api, actualsThrough]);

  useEffect(() => {
    fetchData();
  }, [fetchData, project]);

  const timeDetail = useTimeEntriesReportURL({
    query: {
      start: 'not_set',
      end: moment(actualsThrough).format(dateFormats.isoDate),
      projectId: project.id,
      memberId: member.id,
      currency: project.currency,
    },
  });

  const url = {
    total: timeDetail(),
    billable: timeDetail({ timeBillableTypeId: 'billable' }),
    non_billable: timeDetail({ timeBillableTypeId: project.client.isInternal ? 'internal' : 'non_billable' }),
  };

  const boxes = {
    forecast: data?.forecast?.dates.length > 0,
    get any() {
      return this.forecast;
    },
  };

  const hoursFormat = useHoursFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <Widget loading={!isReady}>
      <Widget.Preview style={{ minHeight: '32.4rem' }} />
      <Widget.Content>
        <ViewNav view={view} onViewChange={onViewChange} />

        {data && (
          <>
            <ChartContainer>
              {data &&
                {
                  progress: () => <MyHoursProgressChart project={project} data={data} />,
                  month: () => <MyHoursByMonthChart project={project} data={data} />,
                }[view]()}
            </ChartContainer>
            <Boxes>
              {project.isBillable && (
                <Box>
                  <UpperBox>
                    <RouteLink to={url.billable}>
                      <Value>
                        <Hours value={data.actual.billable} minimumFractionDigits={0} />
                      </Value>
                      <Label>Actual Billable Hours</Label>
                    </RouteLink>
                  </UpperBox>

                  {boxes.any && (
                    <LowerBoxes>
                      {boxes.forecast && (
                        <SmallBox>
                          <Metric>
                            <Tooltip
                              message={`${hoursFormat.format(data.actual.billable)} actual hours plus ${hoursFormat.format(data.forecast.billable.left)} remaining plan hours.`}
                              placement="top">
                              <Value>
                                <Hours value={data.forecast.billable.forecasted} minimumFractionDigits={0} />
                              </Value>
                              <Label>Forecast</Label>
                            </Tooltip>
                          </Metric>
                          <Metric>
                            <Value>
                              <Hours value={data.forecast.billable.left} minimumFractionDigits={0} />
                            </Value>
                            <Label>Plan Left</Label>
                          </Metric>
                        </SmallBox>
                      )}
                    </LowerBoxes>
                  )}
                </Box>
              )}

              <Box>
                <UpperBox>
                  <RouteLink to={url.non_billable}>
                    <Value>
                      <Hours value={data.actual.nonBillable} minimumFractionDigits={0} />
                    </Value>
                    <Label>Actual Non-billable Hours</Label>
                  </RouteLink>
                </UpperBox>

                {boxes.any && (
                  <LowerBoxes>
                    {boxes.forecast && (
                      <SmallBox>
                        <Metric>
                          <Tooltip
                            message={`${hoursFormat.format(data.actual.nonBillable)} actual hours plus ${hoursFormat.format(data.forecast.nonBillable.left)} remaining plan hours.`}
                            placement="top">
                            <Value>
                              <Hours value={data.forecast.nonBillable.forecasted} minimumFractionDigits={0} />
                            </Value>
                            <Label>Forecast</Label>
                          </Tooltip>
                        </Metric>
                        <Metric>
                          <Value>
                            <Hours value={data.forecast.nonBillable.left} minimumFractionDigits={0} />
                          </Value>
                          <Label>Plan Left</Label>
                        </Metric>
                      </SmallBox>
                    )}
                  </LowerBoxes>
                )}
              </Box>

              <Box>
                <UpperBox>
                  <RouteLink to={url.total}>
                    <Value>
                      <Hours value={data.actual.total} minimumFractionDigits={0} />
                    </Value>
                    <Label>Actual Total Hours</Label>
                  </RouteLink>
                </UpperBox>

                {boxes.any && (
                  <LowerBoxes>
                    {boxes.forecast && (
                      <SmallBox>
                        <Metric>
                          <Tooltip
                            message={`${hoursFormat.format(data.actual.total)} actual hours plus ${hoursFormat.format(data.forecast.total.left)} remaining plan hours.`}
                            placement="top">
                            <Value>
                              <Hours value={data.forecast.total.forecasted} minimumFractionDigits={0} />
                            </Value>
                            <Label>Forecast</Label>
                          </Tooltip>
                        </Metric>
                        <Metric>
                          <Value>
                            <Hours value={data.forecast.total.left} minimumFractionDigits={0} />
                          </Value>
                          <Label>Plan Left</Label>
                        </Metric>
                      </SmallBox>
                    )}
                  </LowerBoxes>
                )}
              </Box>
            </Boxes>
          </>
        )}
      </Widget.Content>
    </Widget>
  );
}
